import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { HD_LANG_RECOMPUTE_ON_LOAD, HdContentLanguageService } from '@/app/human-design/chart/bodygraph/hd-content-language.service';
import { HdGateInfoService } from '@/app/human-design/chart/bodygraph/hd-gate-info.service';
import { HdBodygraphCenter } from '@/app/human-design/chart/bodygraph/hd-bodygraph.models';
import {
  HD_CYCLES_CHEAT_SHEET_CARDS,
  HD_CYCLE_CENTER_CONDITIONING_COLUMNS,
  HD_CYCLE_CENTER_CONDITIONING_FA_ICON,
  HD_CYCLE_ENCOUNTER_RENEWAL_FA_ICON,
  HD_CYCLE_MAIN_KEYNOTES,
  HD_CYCLE_PLANET_GROUPS,
  HdCyclesCheatSheetCardKey,
  HdCycleMainKeynoteKey,
  HdCyclePlanetGroupKey,
} from '@/app/human-design/chart/bodygraph/hd-cycles-cheat-sheet-metadata';
import { getHdPlanetDisplayConfig } from '@/app/human-design/chart/bodygraph/planet-grid/hd-planet-display-config';
import { HdActivePaletteService } from '@/app/human-design/chart/bodygraph/hd-active-palette.service';

/** Main-keynote-kaart (rij 1) in de content-taal. */
interface CyclesKeynoteCardVm {
  readonly key: HdCycleMainKeynoteKey;
  readonly color: string;
  readonly faIcon: string;
  readonly title: string;
  /** HTML met `<b>`-accenten (uit de i18n-scope, via `[innerHTML]`). */
  readonly brings: string;
  readonly bullets: readonly string[];
}

/** Planeet-groep-band (rij 2) in de content-taal. */
interface CyclesPlanetGroupVm {
  readonly key: HdCyclePlanetGroupKey;
  readonly faIcon: string;
  readonly title: string;
  /** Astronomicon-glyphs van de planeten in de groep. */
  readonly glyphs: readonly string[];
}

/** Eén center-item in de "Niet zelf center thema's"-kaart. */
interface CyclesCenterThemeVm {
  readonly center: HdBodygraphCenter;
  readonly label: string;
  /** HTML met `<b>`-accenten. */
  readonly text: string;
  readonly shape: SafeHtml;
}

/** Return-/info-kaart (rijen 4-6) in de content-taal. */
interface CyclesReturnCardVm {
  readonly key: HdCyclesCheatSheetCardKey;
  readonly wide: boolean;
  /** Astronomicon-glyph van de cyclusplaneet, of `null` → FA-icoon. */
  readonly glyph: string | null;
  readonly faIcon: string | null;
  readonly title: string;
  readonly subtitle: string;
  readonly bullets: readonly string[];
  readonly bullets2: readonly string[];
}

/**
 * Pagina die de Cycles cheat sheet toont, nagebouwd uit het Figma-design
 * "Human Design - CYCLES" (zonder de A3-header/branding-footer). Bovenaan de
 * drie main keynotes (Persoonlijk/Educatief/Omgevend) met de planeet-groepen
 * eronder, daarna Tegenkoming-vs-Vernieuwing + de niet-zelf center thema's van
 * een cyclus, en tot slot de return-kaarten (Solar/Saturn/Uranus/Kiron) met de
 * Algemeen- en programma-info.
 *
 * Content komt uit de `human-design/cycles` Transloco-scope via
 * {@link HdGateInfoService.cyclesCheatSheetTextIn}; structuur (kleuren, iconen,
 * glyphs, kolommen) uit `hd-cycles-cheat-sheet-metadata`. Taalwissel via de
 * gedeelde content-taal-picker; nog niet vertaalde EN-velden vallen terug op NL.
 */
@Component({
  selector: 'hd-cycles-cheat-sheet-page',
  standalone: false,
  templateUrl: './hd-cycles-cheat-sheet-page.component.html',
  styleUrl: './hd-cycles-cheat-sheet-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HdCyclesCheatSheetPageComponent {
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly centerColors = inject(HdActivePaletteService);

  protected readonly encounterRenewalIcon = HD_CYCLE_ENCOUNTER_RENEWAL_FA_ICON;
  protected readonly centerConditioningIcon = HD_CYCLE_CENTER_CONDITIONING_FA_ICON;

  private readonly lang = computed(() => this.contentLang.effectiveLang(), HD_LANG_RECOMPUTE_ON_LOAD);

  protected readonly pageTitle = computed(() => this.text('pageTitle'));
  protected readonly bringsLabel = computed(() => this.text('bringsLabel'));

  protected readonly keynoteCards = computed<readonly CyclesKeynoteCardVm[]>(() =>
    HD_CYCLE_MAIN_KEYNOTES.map(info => ({
      key: info.key,
      color: info.color,
      faIcon: info.faIcon,
      title: this.text(`mainKeynotes.${info.key}.title`),
      brings: this.text(`mainKeynotes.${info.key}.brings`),
      bullets: this.list(`mainKeynotes.${info.key}.bullets`),
    })),
  );

  protected readonly planetGroups = computed<readonly CyclesPlanetGroupVm[]>(() =>
    HD_CYCLE_PLANET_GROUPS.map(info => ({
      key: info.key,
      faIcon: info.faIcon,
      title: this.text(`planetGroups.${info.key}.title`),
      glyphs: info.planets.map(planet => getHdPlanetDisplayConfig(planet).astronomiconChar),
    })),
  );

  protected readonly encounterRenewal = computed(() => ({
    title: this.text('encounterRenewal.title'),
    encounter: this.text('encounterRenewal.encounter'),
    renewal: this.text('encounterRenewal.renewal'),
  }));

  protected readonly centerConditioningTitle = computed(() => this.text('centerConditioning.title'));

  protected readonly centerColumns = computed<readonly (readonly CyclesCenterThemeVm[])[]>(() =>
    HD_CYCLE_CENTER_CONDITIONING_COLUMNS.map(column =>
      column.map(center => ({
        center,
        label: this.text(`centerConditioning.centers.${center}.label`),
        text: this.text(`centerConditioning.centers.${center}.text`),
        shape: this.centerColors.iconHtml(center, 'display:block;width:auto;height:100%;overflow:visible;margin:0 auto'),
      })),
    ),
  );

  protected readonly returnCards = computed<readonly CyclesReturnCardVm[]>(() =>
    HD_CYCLES_CHEAT_SHEET_CARDS.map(info => ({
      key: info.key,
      wide: info.wide,
      glyph: info.glyphPlanet == null ? null : getHdPlanetDisplayConfig(info.glyphPlanet).astronomiconChar,
      faIcon: info.faIcon,
      title: this.text(`cards.${info.key}.title`),
      subtitle: this.text(`cards.${info.key}.subtitle`),
      bullets: this.list(`cards.${info.key}.bullets`),
      bullets2: this.list(`cards.${info.key}.bullets2`),
    })),
  );

  constructor() {
    // Beide content-talen eager laden zodat de taalwissel én de NL-fallback voor
    // nog-niet-vertaalde EN-velden meteen correcte tekst opleveren.
    this.gateInfo.loadScopesForLang('nl');
    this.gateInfo.loadScopesForLang('en');
  }

  protected print(): void {
    window.print();
  }

  /** Scope-tekst in de content-taal, met NL-fallback voor (nog) lege EN-velden. */
  private text(key: string): string {
    const lang = this.lang();
    const value = this.gateInfo.cyclesCheatSheetTextIn(key, lang);
    if (value !== '' || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetTextIn(key, 'nl');
  }

  /** Scope-string-array in de content-taal, met dezelfde NL-fallback als {@link text}. */
  private list(key: string): readonly string[] {
    const lang = this.lang();
    const value = this.gateInfo.cyclesCheatSheetListIn(key, lang);
    if (value.length > 0 || lang === 'nl') {
      return value;
    }
    return this.gateInfo.cyclesCheatSheetListIn(key, 'nl');
  }
}
